// chat-session/client/src/utilities/aiChatOrigin.ts
//
// When the user clicks a cited source they're forwarded to the book reader (a
// separate top-level tab on our domain). We want the reader's "Go to AI Chat"
// link to send them back to where they came from — the Chat-AI circle, or, when
// the chat is embedded as an iframe, the host page that embedded it.
//
// We carry that origin in the QUERY STRING of the reader link, NOT a cookie:
// the chat often runs as a cross-site iframe (e.g. embedded on a third-party
// site), where browsers block/partition third-party document.cookie writes, so
// a cookie set here would never reach the reader tab. The reader link is a
// top-level navigation to our own domain, so its URL is immune to that.
//
// The reader side lives in lib-core
// (lib-core/src/shared/domain/ai-chat/aiChatOrigin.ts) and reads these same
// param names. Keep them in sync.

import { isInIframe } from "@/utilities/browserDetection";

export const AI_CHAT_ORIGIN_PARAM = 'cair_origin' // Chat-AI circle id
export const AI_CHAT_HOST_PARAM = 'cair_host'      // embedding host page url

// Known embedders whose default referrer policy only gives us their origin
// (not the exact page). For these we redirect to a fixed page. Add a line here
// per client; anything not listed keeps the referrer/origin behavior.
//
// `circleId` is an optional fallback: Safari often strips the cross-origin
// referrer entirely (and window.top is unreadable), leaving us no host to map.
// When that happens we resolve the host from the Chat-AI circle id instead, so
// citation links still return the user to the licensee site. Fill in the
// circle id for each embedded client.
const KNOWN_HOST_PAGES: { domain: string; url: string; circleId?: string }[] = [
    { domain: 'prestoncoopeducationcentre.org', url: 'https://prestoncoopeducationcentre.org/ask-anything/' },
    { domain: 'ibrahimicollection.com', url: 'https://ibrahimicollection.com/node/2543' /*, circleId: '<IC Ask-Anything circle id>' */ },
]

// If `host` belongs to a known embedder, swap it for that client's exact page;
// otherwise return it unchanged.
const mapKnownHost = (host: string): string => {
    if (!host) return host
    let hostname = ''
    try {
        hostname = new URL(host).hostname.toLowerCase()
    } catch {
        return host
    }
    const match = KNOWN_HOST_PAGES.find(
        (k) => hostname === k.domain || hostname.endsWith('.' + k.domain),
    )
    return match ? match.url : host
}

// The page that embedded the chat iframe, so the reader can return the user
// there instead of to our standalone chat page. Empty when not embedded.
const hostPageUrl = (circleId?: string): string => {
    if (!isInIframe()) return ''
    let host = ''
    // Same-origin embed (e.g. our own dashboard chat view): use the real top URL.
    try {
        const top = window.top
        if (top && top.location?.href) host = top.location.href
    } catch {
        // Cross-origin parent — can't read it; fall through to the referrer.
    }
    // Cross-origin embed: the document that loaded this iframe is the host page
    // (often trimmed to origin-only by the browser's default referrer policy).
    if (!host && /^https?:\/\//i.test(document.referrer)) host = document.referrer
    const mapped = mapKnownHost(host)
    if (mapped) return mapped
    // No host at all (Safari stripped the referrer and window.top is unreadable):
    // fall back to the configured host page for this circle, if any.
    if (circleId) {
        const byCircle = KNOWN_HOST_PAGES.find((k) => k.circleId === circleId)
        if (byCircle) return byCircle.url
    }
    return ''
}

// Query string (with leading '?') to append to the book-reader link so the
// reader knows the origin circle and, when embedded, the host page. Returns ''
// when there's nothing to carry.
export const aiChatOriginQuery = (circleId?: string): string => {
    const params = new URLSearchParams()
    if (circleId) params.set(AI_CHAT_ORIGIN_PARAM, circleId)
    const host = hostPageUrl(circleId)
    if (host) params.set(AI_CHAT_HOST_PARAM, host)
    const qs = params.toString()
    return qs ? `?${qs}` : ''
}
